import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/layout/Layout';
import api from '../api';
import { FaCheckCircle, FaSyncAlt, FaWallet } from 'react-icons/fa';
import PageHeader from '../components/PageHeader';
import ConfirmDialog from '../components/ConfirmDialog';

const COLORS = {
  forestGreen: '#0F3D2E',
  heritageGold: '#C9A14A',
  offWhite: '#FFFDF7',
  softBorder: '#E6D8BE',
  mutedText: '#746A5B',
  success: '#1F8F4D',
  warning: '#B85C38',
  danger: '#D9534F',
};

const extractPayouts = response => {
  if (Array.isArray(response?.data?.payouts)) return response.data.payouts;
  if (Array.isArray(response?.data?.data?.payouts)) return response.data.data.payouts;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const money = value => `R${Number(value || 0).toFixed(2)}`;

const dateOnly = value => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
};

const dateTime = value => (value ? new Date(value).toLocaleString() : '-');
const PAGE_SIZE = 10;

const getRiderName = payout => {
  const rider = payout.rider || {};
  return `${rider.firstName || ''} ${rider.lastName || ''}`.trim() || 'Rider';
};

const getPayoutTips = payout => {
  const savedTips = Number(payout.totalTips || payout.tips || 0);
  if (savedTips > 0) return savedTips;

  return (payout.orders || []).reduce(
    (sum, order) => sum + Number(order.totalTip || order.totalTips || order.tipAmount || 0),
    0,
  );
};

const StatusBadge = ({status}) => {
  const paid = status === 'paid';

  return (
    <span
      style={{
        ...styles.badge,
        backgroundColor: paid ? '#E7F6ED' : '#FFF1E8',
        color: paid ? COLORS.success : COLORS.warning,
      }}>
      {paid ? 'Paid' : 'Pending'}
    </span>
  );
};

export default function RiderPayouts() {
  const [payouts, setPayouts] = useState([]);
  const [status, setStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);
  const [error, setError] = useState('');
  const [confirmPayout, setConfirmPayout] = useState(null);
  const [page, setPage] = useState(1);

  const loadPayouts = async (nextStatus = status) => {
    try {
      setLoading(true);
      setError('');
      const response = await api.getRiderPayouts(nextStatus);
      setPayouts(extractPayouts(response));
    } catch (err) {
      console.error('Failed to load rider payouts:', err);
      setError(err.message || 'Could not load rider payouts.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPayouts(status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    setPage(1);
  }, [status, payouts.length]);

  const totals = useMemo(() => {
    return payouts.reduce(
      (acc, payout) => {
        acc.amount += Number(payout.amount || 0);
        acc.deliveryFees += Number(payout.totalDeliveryFees || 0);
        acc.tips += getPayoutTips(payout);
        acc.deliveries += Number(payout.deliveriesCount || 0);
        return acc;
      },
      {amount: 0, deliveryFees: 0, tips: 0, deliveries: 0},
    );
  }, [payouts]);

  const handleMarkPaid = async payout => {
    try {
      setActionId(payout.id);
      await api.markRiderPayoutPaid(payout.id);
      await loadPayouts(status);
      setConfirmPayout(null);
    } catch (err) {
      console.error('Failed to mark payout paid:', err);
      setError(err.message || 'Could not mark payout as paid.');
    } finally {
      setActionId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(payouts.length / PAGE_SIZE));
  const paginatedPayouts = payouts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Layout>
      <PageHeader
        eyebrow="Rider Finance"
        title="Weekly Rider Payouts"
        subtitle="Pay riders on Monday for the completed Saturday-Friday payout week."
        action={
          <button
            type="button"
            style={styles.refreshButton}
            onClick={() => loadPayouts(status)}
            disabled={loading}>
            <FaSyncAlt size={14} />
            Refresh
          </button>
        }
      />

      <div style={styles.summaryGrid}>
        <div style={styles.summaryCard}>
          <span style={styles.summaryLabel}>Total Payout</span>
          <strong style={styles.summaryValue}>{money(totals.amount)}</strong>
        </div>
        <div style={styles.summaryCard}>
          <span style={styles.summaryLabel}>Delivery Fees</span>
          <strong style={styles.summaryValue}>{money(totals.deliveryFees)}</strong>
        </div>
        <div style={styles.summaryCard}>
          <span style={styles.summaryLabel}>Tips</span>
          <strong style={styles.summaryValue}>{money(totals.tips)}</strong>
        </div>
        <div style={styles.summaryCard}>
          <span style={styles.summaryLabel}>Deliveries</span>
          <strong style={styles.summaryValue}>{totals.deliveries}</strong>
        </div>
      </div>

      <div style={styles.toolbar}>
        <div style={styles.segment}>
          {['pending', 'paid', ''].map(value => (
            <button
              key={value || 'all'}
              type="button"
              onClick={() => setStatus(value)}
              style={{
                ...styles.segmentButton,
                ...(status === value ? styles.segmentButtonActive : {}),
              }}>
              {value ? value[0].toUpperCase() + value.slice(1) : 'All'}
            </button>
          ))}
        </div>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      <div style={styles.tableShell}>
        {loading ? (
          <div style={styles.emptyState}>Loading rider payouts...</div>
        ) : payouts.length === 0 ? (
          <div style={styles.emptyState}>
            <FaWallet size={28} color={COLORS.heritageGold} />
            <strong>No rider payouts found.</strong>
            <span>Completed weekly payouts will appear here after Friday 23:59.</span>
          </div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Rider</th>
                <th style={styles.th}>Period</th>
                <th style={styles.th}>Payable</th>
                <th style={styles.th}>Deliveries</th>
                <th style={styles.th}>Tips</th>
                <th style={styles.th}>Amount</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedPayouts.map(payout => (
                <tr key={payout.id} style={styles.row}>
                  <td style={styles.td}>
                    <strong>{getRiderName(payout)}</strong>
                    <div style={styles.muted}>{payout.rider?.phone || payout.rider?.email || '-'}</div>
                  </td>
                  <td style={styles.td}>
                    {dateOnly(payout.periodStart)} - {dateOnly(payout.periodEnd)}
                  </td>
                  <td style={styles.td}>{dateOnly(payout.payableOn)}</td>
                  <td style={styles.td}>
                    <strong>{payout.deliveriesCount || 0}</strong>
                    <div style={styles.muted}>orders included</div>
                  </td>
                  <td style={styles.td}>
                    <strong>{money(getPayoutTips(payout))}</strong>
                  </td>
                  <td style={styles.td}>
                    <strong>{money(payout.amount)}</strong>
                    <div style={styles.muted}>
                      Fees {money(payout.totalDeliveryFees)}
                    </div>
                  </td>
                  <td style={styles.td}>
                    <StatusBadge status={payout.status} />
                    {payout.paidAt && <div style={styles.muted}>{dateTime(payout.paidAt)}</div>}
                  </td>
                  <td style={styles.td}>
                    {payout.status === 'paid' ? (
                      <span style={styles.paidDone}>
                        <FaCheckCircle size={14} />
                        Done
                      </span>
                    ) : (
                      <button
                        type="button"
                        style={styles.payButton}
                        onClick={() => setConfirmPayout(payout)}
                        disabled={actionId === payout.id}>
                        {actionId === payout.id ? 'Saving...' : 'Mark Paid'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && payouts.length > 0 && (
          <div style={styles.paginationBar}>
            <button
              type="button"
              style={styles.pageButton}
              disabled={page === 1}
              onClick={() => setPage(current => Math.max(1, current - 1))}>
              Previous
            </button>
            <span style={styles.pageText}>Page {page} of {totalPages}</span>
            <button
              type="button"
              style={styles.pageButton}
              disabled={page === totalPages}
              onClick={() => setPage(current => Math.min(totalPages, current + 1))}>
              Next
            </button>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!confirmPayout}
        title="Mark rider payout paid"
        message={
          confirmPayout
            ? `Mark ${money(confirmPayout.amount)} as paid for ${getRiderName(
                confirmPayout,
              )}? Only do this after the money has been paid to the rider account.`
            : ''
        }
        confirmLabel="Mark paid"
        tone="success"
        loading={!!actionId}
        onCancel={() => setConfirmPayout(null)}
        onConfirm={() => handleMarkPaid(confirmPayout)}
      />
    </Layout>
  );
}

const styles = {
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 20,
    marginBottom: 24,
  },
  kicker: {
    margin: '0 0 6px',
    color: COLORS.heritageGold,
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    fontSize: 12,
  },
  title: {
    margin: 0,
    color: COLORS.forestGreen,
    fontSize: 30,
    fontWeight: 900,
  },
  subtitle: {
    margin: '8px 0 0',
    color: COLORS.mutedText,
  },
  refreshButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    backgroundColor: COLORS.forestGreen,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '11px 14px',
    fontWeight: 900,
    cursor: 'pointer',
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 14,
    marginBottom: 20,
  },
  summaryCard: {
    backgroundColor: COLORS.offWhite,
    border: `1px solid ${COLORS.softBorder}`,
    borderRadius: 8,
    padding: 16,
  },
  summaryLabel: {
    display: 'block',
    color: COLORS.mutedText,
    fontSize: 12,
    fontWeight: 800,
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  summaryValue: {
    color: COLORS.forestGreen,
    fontSize: 24,
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 14,
  },
  segment: {
    display: 'inline-flex',
    backgroundColor: '#EFE6D3',
    padding: 4,
    borderRadius: 8,
    border: `1px solid ${COLORS.softBorder}`,
  },
  segmentButton: {
    border: 'none',
    backgroundColor: 'transparent',
    color: COLORS.mutedText,
    borderRadius: 6,
    padding: '9px 14px',
    fontWeight: 900,
    cursor: 'pointer',
  },
  segmentButtonActive: {
    backgroundColor: COLORS.offWhite,
    color: COLORS.forestGreen,
    boxShadow: '0 1px 4px rgba(15,61,46,0.12)',
  },
  errorBox: {
    backgroundColor: '#FCEAE8',
    color: COLORS.danger,
    border: '1px solid #F4B8B4',
    borderRadius: 8,
    padding: 12,
    marginBottom: 14,
    fontWeight: 800,
  },
  tableShell: {
    backgroundColor: COLORS.offWhite,
    border: `1px solid ${COLORS.softBorder}`,
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: '0 8px 28px rgba(15, 61, 46, 0.08)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    backgroundColor: '#F1E7D2',
    color: COLORS.forestGreen,
    textAlign: 'left',
    padding: '14px 16px',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  row: {
    borderTop: `1px solid ${COLORS.softBorder}`,
  },
  td: {
    padding: '15px 16px',
    color: COLORS.forestGreen,
    verticalAlign: 'top',
  },
  muted: {
    color: COLORS.mutedText,
    fontSize: 12,
    marginTop: 4,
  },
  badge: {
    display: 'inline-block',
    borderRadius: 999,
    padding: '6px 10px',
    fontSize: 12,
    fontWeight: 900,
    textTransform: 'uppercase',
  },
  payButton: {
    backgroundColor: COLORS.heritageGold,
    color: COLORS.forestGreen,
    border: 'none',
    borderRadius: 8,
    padding: '9px 12px',
    cursor: 'pointer',
    fontWeight: 900,
  },
  paidDone: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    color: COLORS.success,
    fontWeight: 900,
  },
  emptyState: {
    minHeight: 220,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    color: COLORS.mutedText,
    textAlign: 'center',
    padding: 24,
  },
  paginationBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
    padding: 12,
    borderTop: `1px solid ${COLORS.softBorder}`,
    backgroundColor: '#FBF6EA',
  },
  pageButton: {
    border: `1px solid ${COLORS.softBorder}`,
    backgroundColor: COLORS.offWhite,
    color: COLORS.forestGreen,
    borderRadius: 6,
    padding: '8px 11px',
    fontWeight: 900,
    cursor: 'pointer',
  },
  pageText: {
    color: COLORS.mutedText,
    fontWeight: 800,
    fontSize: 13,
  },
};
